import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { activityLogApi } from '../lib/api';
import api from '../lib/api';
import { Download, RefreshCw } from 'lucide-react';

type Period = 'today' | 'week' | 'custom';

const ALERT_TYPES = [
  'ESCALATION',
  'QC_PHASE1_REVIEWED',
  'QC_PHASE2_REVIEWED',
  'DEADLINE_MISSED',
  'PRIORITY_CHANGE',
  'T60_REMINDER',
];

const ADMIN_ROLES = ['admin', 'ceo', 'head_media_buying', 'creative_head', 'assistant_team_lead'];

interface AlertLog {
  id: string;
  created_at: string;
  action_type: string;
  resource_type: string;
  description?: string;
  metadata?: any;
  user_name?: string;
  user_email?: string;
  file_request_title?: string;
}

function downloadCsv(rows: Record<string, unknown>[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]).join(',');
  const body = rows.map(r => Object.values(r).map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([headers + '\n' + body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function getTypeBadgeClass(actionType: string) {
  if (actionType === 'ESCALATION') return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400';
  if (actionType === 'QC_PHASE1_REVIEWED' || actionType === 'QC_PHASE2_REVIEWED') return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400';
  if (actionType === 'DEADLINE_MISSED') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400';
  if (actionType === 'PRIORITY_CHANGE') return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400';
  if (actionType === 'T60_REMINDER') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400';
  return 'bg-muted text-muted-foreground';
}

function getPeriodDates(period: Period, customFrom: string, customTo: string): { from: string; to: string } {
  const now = new Date();
  if (period === 'today') {
    const today = now.toISOString().split('T')[0];
    return { from: today, to: today };
  }
  if (period === 'week') {
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    return { from: weekAgo.toISOString().split('T')[0], to: now.toISOString().split('T')[0] };
  }
  return { from: customFrom, to: customTo };
}

export function AlertsDashboardPage() {
  const { user } = useAuth();
  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const allRoles = [user?.role, ...additionalRoles].filter(Boolean) as string[];
  const isAdminish = allRoles.some(r => ADMIN_ROLES.includes(r));

  const [period, setPeriod] = useState<Period>('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [logs, setLogs] = useState<AlertLog[]>([]);
  const [loading, setLoading] = useState(false);

  // Escalation export
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    const { from, to } = getPeriodDates(period, customFrom, customTo);
    if (period === 'custom' && (!from || !to)) return;
    setLoading(true);
    try {
      const res = await activityLogApi.getLogs({
        date_from: from,
        date_to: to,
        limit: 500,
      });
      const all: AlertLog[] = res.data?.data?.logs || res.data?.logs || [];
      // Filter client-side to alert types
      const filtered = all.filter(log => ALERT_TYPES.includes(log.action_type));
      setLogs(filtered);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const kpi = {
    total: logs.length,
    escalations: logs.filter(l => l.action_type === 'ESCALATION').length,
    missed: logs.filter(l => l.action_type === 'DEADLINE_MISSED').length,
    qcFails: logs.filter(l => l.action_type === 'QC_PHASE1_REVIEWED' || l.action_type === 'QC_PHASE2_REVIEWED').length,
    priority: logs.filter(l => l.action_type === 'PRIORITY_CHANGE').length,
  };

  const handleExport = async () => {
    if (!exportFrom || !exportTo) { alert('Please set both From and To dates'); return; }
    setExportLoading(true);
    try {
      const res = await api.get('/activity-logs/escalation-export', {
        params: { dateFrom: exportFrom, dateTo: exportTo },
      });
      const rows = res.data?.data || [];
      if (!rows.length) { alert('No escalation data for selected period'); return; }
      downloadCsv(rows, `escalation-report-${exportFrom}-${exportTo}.csv`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to export');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <DashboardLayout title="Alerts Dashboard">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Period selector */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          {(['today', 'week', 'custom'] as Period[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 text-sm rounded-full border font-medium transition-colors ${
                period === p ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : 'Custom'}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
              />
            </div>
          )}
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total Alerts', value: kpi.total },
            { label: 'Escalations', value: kpi.escalations },
            { label: 'Missed Deadlines', value: kpi.missed },
            { label: 'QC Fails', value: kpi.qcFails },
            { label: 'Priority Changes', value: kpi.priority },
          ].map(stat => (
            <div key={stat.label} className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-foreground">{stat.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Alert Feed */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading alerts...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">No alerts for the selected period</div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead className="bg-muted/60">
                <tr>
                  <th className="text-left p-3 font-semibold text-muted-foreground">Time</th>
                  <th className="text-left p-3 font-semibold text-muted-foreground">Type</th>
                  <th className="text-left p-3 font-semibold text-muted-foreground">File Request / Title</th>
                  <th className="text-left p-3 font-semibold text-muted-foreground">Editor / User</th>
                  <th className="text-left p-3 font-semibold text-muted-foreground">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log.id} className={`border-t border-border ${i % 2 === 1 ? 'bg-muted/20' : ''}`}>
                    <td className="p-3 text-muted-foreground whitespace-nowrap text-xs">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${getTypeBadgeClass(log.action_type)}`}>
                        {log.action_type}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground truncate max-w-xs">
                      {log.file_request_title || log.resource_type || '—'}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {log.user_name || log.user_email || '—'}
                    </td>
                    <td className="p-3 text-muted-foreground text-xs">
                      {log.description || (log.metadata ? JSON.stringify(log.metadata).slice(0, 80) : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Escalation Export (admin only) */}
        {isAdminish && (
          <div className="bg-muted/30 border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-3">Escalation Export</h2>
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">From</label>
                <input
                  type="date"
                  value={exportFrom}
                  onChange={e => setExportFrom(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">To</label>
                <input
                  type="date"
                  value={exportTo}
                  onChange={e => setExportTo(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                />
              </div>
              <div className="mt-4">
                <Button size="sm" onClick={handleExport} disabled={exportLoading}>
                  <Download size={13} className="mr-1.5" />
                  {exportLoading ? 'Exporting...' : 'Export Report'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
